/**
 * `К2.1` (`АРХ-Н16`): блокировка и разблокировка учётной записи с сайта.
 *
 * Правила те же, что у back (`account-blocking-service.js`): root — любого,
 * оператор организации — только своих; себя — никому. Здесь только то, что
 * решает, показывать ли кнопку, и как назвать состояние; сам отказ всё равно
 * приходит из back и показывается его текстом.
 */

export type AccountActor = {
  id?: number | string | null;
  role?: string | null;
  company_id?: number | null;
};

export type AccountTarget = {
  id?: number | string | null;
  company_id?: number | null;
  account_state?: string | null;
};

/** `account-state.js:24–41`: те же состояния и те же подписи. */
export const ACCOUNT_STATE_LABELS: Record<string, string> = {
  pending: "приглашён",
  active: "активна",
  access_closed: "доступ закрыт",
  blocked: "заблокирована",
  deletion_requested: "поставлена на удаление",
  cancelled: "аннулирована",
  anonymized: "обезличена",
};

export function accountStateLabel(state: string | null | undefined): string {
  if (!state) return "—";

  return ACCOUNT_STATE_LABELS[state] ?? state;
}

export function isBlockedAccount(state: string | null | undefined): boolean {
  return state === "blocked";
}

/**
 * Видна ли кнопка блокировки у этой строки.
 *
 * root — у всех, кроме себя; оператор организации — у своих, кроме себя;
 * остальным ролям — нет (маршрут их пускает, но получили бы 403).
 */
export function canBlockUser(
  actor: AccountActor | null | undefined,
  target: AccountTarget | null | undefined,
): boolean {
  if (!actor || !target) return false;

  if (actor.role !== "root" && actor.role !== "оператор организации") {
    return false;
  }

  if (String(actor.id) === String(target.id)) return false;

  if (actor.role === "root") return true;

  if (actor.company_id === null || actor.company_id === undefined) {
    return false;
  }

  return Number(actor.company_id) === Number(target.company_id);
}

/** Тело запроса: пустая причина не отправляется вовсе. */
export function blockRequestOf(reason: string | null | undefined): {
  reason?: string;
} {
  const text = String(reason ?? "").trim();

  return text ? { reason: text } : {};
}

/** Новое состояние строки без перезагрузки списка. */
export function applyAccountState<T extends AccountTarget>(
  users: T[],
  userId: number | string,
  state: string,
): T[] {
  return users.map((user) =>
    Number(user.id) === Number(userId)
      ? { ...user, account_state: state }
      : user,
  );
}

/** Текст отказа back для окна подтверждения: окно остаётся открытым. */
export function accountBlockErrorText(error: any): string {
  const data = error?.response?.data;

  return (
    data?.message ||
    data?.error ||
    error?.message ||
    "Не удалось изменить состояние учётной записи"
  );
}
